import { DoorPositionEnum, DoorType, ProductTypeEnum, SlidingDoorsType } from "../types/types";
import { SLIDING_DOOR_DEPTH_OFFSET, SLIDING_DOOR_DESK_THICKNESS } from "./MeasurementView";
export default class Store {
    constructor(data) {
        this._deskVertical = [];
        this._deskHorizontal = [];
        this._insideOptions = [];
        this._minCubbyHeight = 0;
        if (data !== undefined) {
            this.setData(data);
        }
    }
    get productType() {
        return this._data.type;
    }
    get isRegale() {
        return this.productType === ProductTypeEnum.Regale;
    }
    get isSideboard() {
        return this.productType === ProductTypeEnum.Sideboard;
    }
    get isWardrobe() {
        return this.productType === ProductTypeEnum.Wardrobe;
    }
    get minCubbyHeight() {
        if (this._minCubbyHeight > 0) {
            return this._minCubbyHeight;
        }
        let cubbyHeight = 0;
        this.horizontalDesks.forEach((desk) => {
            if (cubbyHeight === 0 || cubbyHeight > desk.structure.height) {
                cubbyHeight = desk.structure.height;
            }
        });
        return cubbyHeight;
    }
    get minCubbyWidth() {
        let cubbyWidth = 0;
        this.horizontalDesks.forEach((desk) => {
            if (cubbyWidth === 0 || cubbyWidth > desk.structure.width) {
                cubbyWidth = desk.structure.width;
            }
        });
        return cubbyWidth;
    }
    get width() {
        return this._data.width;
    }
    get height() {
        return this._data.height;
    }
    get depth() {
        return this._data.depth
            - (this.hasSlidingDoors && this.slidingDoors.type === SlidingDoorsType.DOUBLE_RIGHT ? (SLIDING_DOOR_DESK_THICKNESS + SLIDING_DOOR_DEPTH_OFFSET) : 0);
    }
    get socket() {
        return this._data.socket;
    }
    get thickness() {
        return this._data.thickness;
    }
    get aroundThickness() {
        return this._data.thickness.around;
    }
    get isBevel() {
        return this._data.bevel === undefined
            ? false
            : this._data.bevel.position !== 'none';
    }
    get isLeftBevel() {
        return this._bevelType === 'left';
    }
    get bevelHeight() {
        return this.isBevel ? this._data.bevel.y : 0;
    }
    get bevelColumnCount() {
        return this.isBevel ? this._data.bevel.bevelColumnCount : 0;
    }
    get flatColumnCount() {
        return this.isBevel ? this._data.bevel.flatColumnCount : 0;
    }
    get firstBevelDeskIndex() {
        return this.isBevel
            ? this.isLeftBevel ? 0 : this.verticalDesks.length - 1
            : 0;
    }
    get lastBevelDeskIndex() {
        return this.isBevel
            ? this.isLeftBevel ? this.bevelColumnCount : this.flatColumnCount
            : 0;
    }
    get hasContinuousDesks() {
        return this._hasAdditionalOption('durchgehendeDeckBodenplatte');
    }
    get hasSocket() {
        return this._data.socket.id !== 'no-socket';
    }
    get hasFeet() {
        return this._data.socket.id !== 'no-socket' && this._data.socket.id !== 'socket';
    }
    get socketData() {
        return {
            top: this.height - this._data.socket.height,
            left: 0,
            width: this.width,
            height: this._data.socket.height
        };
    }
    get feetData() {
        return {
            height: this._data.socket.height,
            width: this._feetWidth,
            depth: this._feetDepth,
            count: (this.hasFeet && this._data.socket.count) ? Math.ceil(this._data.socket.count / 2) : 0,
            hasFeetInCenter: (this.hasFeet && this._data.socket.count)
                ? this._data.socket.count % 2 === 1
                : false,
        };
    }
    get hasFeetRolls() {
        return this._data.socket.id === '1115ZF50703PU'
            || this._data.socket.id === '105F50703PU';
    }
    get hasFeetMetal5() {
        return this._data.socket.id === 'NZ59-G0004-100'
            || this._data.socket.id === '3291';
    }
    get hasFeetMetal2() {
        return this._data.socket.id.indexOf('4369') > -1;
    }
    get hasFeetMetal4() {
        return this._data.socket.id.indexOf('3G201') > -1;
    }
    get hasFeetMetal6() {
        return this._data.socket.id.indexOf('3816') > -1;
    }
    get hasFeetWoodFrame() {
        return this._data.socket.id === 'wood-frame';
    }
    get hasFeetMetalFrame1() {
        return this._data.socket.id.indexOf('metal-frame1') > -1;
    }
    get hasFeetMetalFrame2() {
        return this._data.socket.id.indexOf('metal-frame2') > -1;
    }
    get hasFeetHairpin() {
        return this._data.socket.id.indexOf('3B04') > -1;
    }
    get hasFeetLongDesk() {
        return this._data.socket.id === 'wood-5';
    }
    get hasSkirtingBoard() {
        const optionName = 'fussleiste';
        return this._hasAdditionalOption(optionName)
            && this._data.additionalOptions[optionName].settings !== undefined
            && this._data.additionalOptions[optionName].settings.height !== undefined
            && this._data.additionalOptions[optionName].settings.depth !== undefined
            && this._data.additionalOptions[optionName].settings.height.value !== undefined
            && this._data.additionalOptions[optionName].settings.height.value !== null
            && this._data.additionalOptions[optionName].settings.depth.value !== undefined
            && this._data.additionalOptions[optionName].settings.depth.value !== null;
    }
    get skirtingBoard() {
        return {
            height: this._data.additionalOptions.fussleiste.settings.height.value,
            width: this._data.additionalOptions.fussleiste.settings.depth.value,
        };
    }
    get bevelData() {
        return {
            topX: this.bevelTopX,
            topY: this.bevelTopY,
            bottomX: this.isLeftBevel ? 0 : this.width,
            bottomY: this.height - this.bevelHeight,
            width: this.bevelWidth,
            angle: Math.atan((this.height - this.bevelHeight) / this.bevelWidth)
        };
    }
    get bottomDeskData() {
        const thickness = this.getHorizontalThickness(true);
        return {
            top: this.height - this.socketData.height - thickness,
            left: this.hasContinuousDesks && this.hasSocket && !this.hasFeet ? this.aroundThickness : 0,
            width: this.width - (this.hasContinuousDesks && this.hasSocket && !this.hasFeet ? (2 * this.aroundThickness) : 0),
            height: thickness
        };
    }
    get leftDimensions() {
        let dimensions = [];
        let dimensionsSortByHeight = [];
        if (!this.hasColumnsSameHeight) {
            this.verticalDesks.forEach((desk, index) => {
                if (dimensionsSortByHeight.filter((q) => desk.structure.height === q.height).length <= 0
                    && desk.structure.height !== this.height) {
                    dimensionsSortByHeight.push({
                        height: desk.underBevel
                            ? this.bevelHeight
                            : desk.structure.height,
                        isBevel: desk.underBevel
                    });
                }
            });
            if (dimensionsSortByHeight.length > 1) {
                dimensionsSortByHeight = [...dimensionsSortByHeight]
                    .filter((d) => !d.isBevel)
                    .sort(this._compare)
                    .reverse();
            }
        }
        dimensions = [
            { height: this.height, isBevel: false },
            ...dimensionsSortByHeight
        ];
        const bevelIndex = dimensions.findIndex((d) => d.isBevel);
        // add bevel dimension
        if (this.isBevel && bevelIndex < 0) {
            dimensions.push({
                height: this.bevelHeight,
                isBevel: true
            });
        }
        // add socket dimension
        if (this.hasSocket) {
            dimensions.push({
                height: this.socketData.height,
                isBevel: false
            });
        }
        return dimensions;
    }
    get verticalDesks() {
        if (this._deskVertical.length > 0) {
            return this._deskVertical;
        }
        this._deskVertical = [];
        let leftOffset = 0;
        let nextHeight = 0;
        this._columns.forEach((column, index) => {
            // create first vertical desk
            if (index === 0) {
                const thickness = this.getVerticalThickness(0);
                this._deskVertical.push({
                    topOffset: 0,
                    leftOffset: leftOffset,
                    height: this._isBevelVerticalDesk(0) ? this.bevelHeight : column.height,
                    width: thickness,
                    thickness: thickness,
                    underBevel: this._isBevelVerticalDesk(0),
                    structure: column,
                    last: false,
                    columnIndex: index,
                });
            }
            // add vertical desk data
            const deskIndex = index + 1;
            leftOffset = leftOffset + this.getVerticalThickness(index) + column.width;
            nextHeight = this._columns[deskIndex] !== undefined ? this._columns[deskIndex].height : 0;
            this._deskVertical.push({
                topOffset: 0,
                leftOffset: leftOffset,
                height: nextHeight > column.height ? nextHeight : column.height,
                width: this.getVerticalThickness(deskIndex),
                thickness: this.getVerticalThickness(deskIndex),
                underBevel: this._isBevelVerticalDesk(deskIndex),
                structure: column,
                last: index === (this._columns.length - 1),
                columnIndex: index,
            });
        });
        if (this.hasVerticalSplits) {
            this.mapVerticalDesksSplits();
        }
        return this._deskVertical;
    }
    /**
     * Return true if has some vertical desk split
     * @private
     * @return boolean
     */
    get hasVerticalSplits() {
        if (typeof this._data.hasDeskSplit === "undefined" || !this._data.hasDeskSplit || typeof this._data.hasDeskSplit.vertical === "undefined") {
            return false;
        }
        return this._data.hasDeskSplit.vertical;
    }
    /**
     *
     * @private
     * @return (null|number)[]
     */
    get verticalSplits() {
        if (typeof this._data.deskSplits === "undefined"
            || !this._data.deskSplits
            || typeof this._data.deskSplits.vertical === "undefined") {
            return [];
        }
        return this._data.deskSplits.vertical;
    }
    /**
     *
     */
    get firstDeskSplit() {
        if (this.verticalSplits.length === 0) {
            return null;
        }
        return this.verticalSplits[0];
    }
    /**
     * Return true if has some horizontal desk split
     * @return boolean
     */
    get hasHorizontalSplits() {
        if (typeof this._data === "undefined"
            || typeof this._data.hasDeskSplit === "undefined"
            || !this._data.hasDeskSplit) {
            return false;
        }
        return this._data.hasDeskSplit.horizontal;
    }
    /**
     *
     * @return (null | number)[][]
     */
    get horizontalTopSplits() {
        if (typeof this._data.deskSplits === "undefined"
            || !this._data.deskSplits
            || typeof this._data.deskSplits.horizontal === "undefined"
            || !this._data.deskSplits.horizontal
            || typeof this._data.deskSplits.horizontal.top === "undefined"
            || !this._data.deskSplits.horizontal.top) {
            return [];
        }
        return this._data.deskSplits.horizontal.top;
    }
    /**
     *
     * @return (null|number)[]
     */
    get horizontalBottomSplits() {
        if (typeof this._data.deskSplits === "undefined"
            || !this._data.deskSplits
            || typeof this._data.deskSplits.horizontal === "undefined"
            || !this._data.deskSplits.horizontal
            || typeof this._data.deskSplits.horizontal.bottom === "undefined"
            || !this._data.deskSplits.horizontal.bottom) {
            return [];
        }
        return this._data.deskSplits.horizontal.bottom;
    }
    /**
     *
     * @return boolean
     */
    get hasHorizontalBottomSplits() {
        return this.horizontalBottomSplits.length > 0;
    }
    mapVerticalDesksSplits() {
        if (this._deskVertical.length > 0) {
            this._deskVertical = this._deskVertical.map((desk, index) => {
                if (typeof this.verticalSplits[index] === "number") {
                    desk.split = this.verticalSplits[index];
                }
                return desk;
            });
        }
    }
    get horizontalDesks() {
        let cubbyIndex = 0;
        let cubbyDoorIndex = 1;
        let totalCubbyCount = 0;
        if (this._deskHorizontal.length > 0) {
            return this._deskHorizontal;
        }
        this._deskHorizontal = [];
        let prevDeskIndex = 0;
        let prevCubbyTotal = 0;
        this.verticalDesks.forEach((column, index) => {
            cubbyIndex = 0;
            let topOffset = this.height;
            if (index > 0) {
                topOffset = topOffset - this.getHorizontalThickness(true);
                totalCubbyCount = totalCubbyCount + column.structure.structure.length;
                // counting cubby number text
                if (prevDeskIndex !== index) {
                    prevDeskIndex = index;
                    prevCubbyTotal = prevCubbyTotal + column.structure.structure.length;
                    if (index > 1) {
                        prevCubbyTotal = prevCubbyTotal + this.verticalDesks[index - 1].structure.structure.length;
                    }
                }
                column.structure.structure.forEach((cubby, i) => {
                    const last = (column.structure.structure.length - 1) === i;
                    topOffset = topOffset - cubby.height - this.getHorizontalThickness(last);
                    this._deskHorizontal.push({
                        topOffset: topOffset,
                        leftOffset: column.leftOffset - column.structure.width,
                        height: this.getHorizontalThickness(last),
                        width: column.structure.width,
                        thickness: this.getHorizontalThickness(last),
                        underBevel: column.underBevel,
                        structure: cubby,
                        column: column,
                        last: last,
                        columnIndex: index,
                        cubbyNumber: prevCubbyTotal
                    });
                    prevCubbyTotal = prevCubbyTotal - 1;
                    const insideCubbyIndex = totalCubbyCount - cubbyIndex;
                    // Prepare data for inside option
                    const groupInsideOptionTypes = this.getInsideOptionTypesByCubbyId(insideCubbyIndex);
                    for (const groupInsideOptionType of groupInsideOptionTypes) {
                        this._insideOptions.push({
                            top: topOffset - this.socket.height + this.aroundThickness,
                            left: column.leftOffset - column.structure.width,
                            width: column.structure.width,
                            height: cubby.height,
                            type: groupInsideOptionType,
                            door: this.getDoorTypeByGroupId(insideCubbyIndex),
                        });
                    }
                    cubbyDoorIndex++;
                    cubbyIndex++;
                });
            }
        });
        return this._deskHorizontal;
    }
    // Find inside option type by cubby id
    getInsideOptionTypesByCubbyId(cubbyIndex) {
        if (this._data.cubbyInsideOptions !== undefined
            && this._data.cubbyInsideOptions.groups !== undefined
            && this._data.cubbyInsideOptions.groups.length > 0) {
            return this._data.cubbyInsideOptions.groups.filter(group => {
                return group.insideOption !== undefined
                    && group.insideOption.length > 0
                    && group.insideOption[0].cubbyIds.length > 0
                    && group.insideOption[0].cubbyIds[0][0] !== undefined
                    && group.insideOption[0].cubbyIds[0][0] === cubbyIndex
                    ? group.settings.type.id : false;
            }).map(group => group.settings.type.id);
        }
        return [];
    }
    // Find door option type by cubby id
    getDoorTypeByGroupId(cubbyIndex) {
        if (this._data.doors !== undefined
            && this._data.doors.groups !== undefined
            && this._data.doors.groups.length > 0) {
            const group = this._data.doors.groups.find((group) => {
                return group.doors !== undefined
                    && group.doors.length > 0
                    && group.doors[0].cubbyIds.length > 0
                    && group.doors[0].cubbyIds[0] !== undefined
                    && group.doors[0].cubbyIds[0].length > 0
                    && group.doors[0].cubbyIds[0].indexOf(cubbyIndex) > -1
                    ? group.settings.type.id : false;
            });
            if (group === undefined) {
                return false;
            }
            return group.settings.type.id;
        }
    }
    getCubby(cubbyId) {
        for (const column of this._columns) {
            const cubby = column.structure.find(cubby => cubby.id === cubbyId);
            if (cubby) {
                return cubby;
            }
        }
        return null;
    }
    // InputDoors
    // prepare doors data
    get doors() {
        let doorsData = [];
        for (let group of this._data.doors.groups) {
            const door = group.doors[0];
            let x = Number.MAX_SAFE_INTEGER;
            let y = Number.MAX_SAFE_INTEGER;
            let width = 0;
            let height = 0;
            for (let i = 0; i < door.cubbyIds.length; i++) {
                const firstCubby = this.getCubby(door.cubbyIds[i][0]);
                if (firstCubby) {
                    width += firstCubby.width + (i > 0 ? this.verticalThickness : 0);
                    x = Math.min(x, firstCubby.x);
                    for (let j = 0; j < door.cubbyIds[i].length; j++) {
                        const cubby = this.getCubby(door.cubbyIds[i][j]);
                        if (cubby) {
                            height += cubby.height + (j > 0 ? this.thickness.horizontal : 0);
                            y = Math.min(y, cubby.y);
                        }
                    }
                }
            }
            const doorIsUnderBevel = this.isHorizontalPositionUnderBevel(group.id, x);
            doorsData.push({
                x,
                y,
                width,
                height,
                id: group.id,
                type: group.settings.type.id,
                position: group.settings.position.id,
                thickness: door.thickness,
                underBevel: doorIsUnderBevel,
                cubbyIds: door.cubbyIds
            });
        }
        if (this.hasSlidingDoors) {
            for (let doorIndex = 0; doorIndex < this.slidingDoors.doors.length; doorIndex++) {
                const slidingDoor = this.slidingDoors.doors[doorIndex];
                const width = slidingDoor.columns.reduce((totalWidth, columnIndex) => totalWidth + this._columns[columnIndex].width, 0)
                    + (slidingDoor.columns.length - 1) * this.verticalThickness;
                let x = this.aroundThickness;
                for (let i = 0; i < slidingDoor.columns[0]; i++) {
                    x += this._columns[i].width + this.verticalThickness;
                }
                let zOffset = SLIDING_DOOR_DEPTH_OFFSET;
                if (this.slidingDoors.type === SlidingDoorsType.DOUBLE_LEFT && doorIndex > 0) {
                    zOffset = -SLIDING_DOOR_DESK_THICKNESS;
                }
                else if (this.slidingDoors.type === SlidingDoorsType.DOUBLE_RIGHT
                    || (this.slidingDoors.type === SlidingDoorsType.TRIPLE && doorIndex === 1)) {
                    zOffset += doorIndex * (SLIDING_DOOR_DESK_THICKNESS + SLIDING_DOOR_DEPTH_OFFSET);
                }
                doorsData.push({
                    x,
                    y: this.aroundThickness,
                    width,
                    height: this._columns[slidingDoor.columns[0]].height - this.socket.height - 2 * this.aroundThickness,
                    id: null,
                    type: DoorType.Sliding,
                    position: DoorPositionEnum.Outer,
                    thickness: SLIDING_DOOR_DESK_THICKNESS,
                    underBevel: false,
                    cubbyIds: slidingDoor.columns.map(columnIndex => this._columns[columnIndex].structure.map(cubby => cubby.id)),
                    zOffset,
                });
            }
        }
        return [...doorsData]
            .sort((a, b) => b.y - a.y)
            .sort((a, b) => a.x - b.x);
    }
    // Outer doors
    get outerDoors() {
        return [...this.doors]
            .filter(d => d.position === DoorPositionEnum.Outer)
            .sort((a, b) => a.x - b.x);
    }
    get outerDoorOffset() {
        let thickness = 0;
        for (let group of this._data.doors.groups.filter(g => g.settings.position.id === DoorPositionEnum.Outer)) {
            const door = group.doors[0];
            if (door.thickness > thickness) {
                thickness = door.thickness;
            }
        }
        return thickness;
    }
    /**
     * Top desk overhang
     * @return number
     */
    get topDeskOverhang() {
        return this._data.topDeskOverhang;
    }
    get outerDoorOrDeskOffset() {
        return Math.max(this.topDeskOverhang, this.outerDoorOffset);
    }
    get minCubbySize() {
        return Math.min(this.minCubbyHeight, (this.minCubbyWidth / 3));
    }
    get hasColumnsSameHeight() {
        if (this.isBevel) {
            return false;
        }
        return this._columns.filter((c, i) => {
            return (i > 0 ? c.height === this._columns[0].height : true);
        }).length === this._columnCount;
    }
    // Get inside option data
    get insideOptions() {
        return this._insideOptions;
    }
    get verticalThickness() {
        return this._data.thickness.vertical;
    }
    get _columns() {
        const ret = [];
        let x = this.aroundThickness;
        for (const column of this._data.structure) {
            let y = this.aroundThickness;
            const newColumn = Object.assign({}, column, { x, y, structure: [] });
            for (const cubby of column.structure) {
                const newCubby = Object.assign({}, cubby, { x, y });
                newColumn.structure.push(newCubby);
                y += cubby.height + this.thickness.horizontal;
            }
            ret.push(newColumn);
            x += column.width + this.verticalThickness;
        }
        return ret;
    }
    get _columnCount() {
        return this._data.structure.length;
    }
    get _bevelType() {
        return this._data.bevel === undefined
            ? 'none'
            : this._data.bevel.position;
    }
    get bevelTopX() {
        return this.isLeftBevel
            ? this._deskVertical[this.bevelColumnCount].leftOffset
            : this._deskVertical[this.flatColumnCount].leftOffset + this.getVerticalThickness(this.flatColumnCount);
    }
    get bevelTopY() {
        return this.isLeftBevel
            ? this._deskVertical[this.bevelColumnCount].topOffset
            : this._deskVertical[this.flatColumnCount].topOffset;
    }
    get bevelWidth() {
        if (this.isBevel) {
            let width = 0;
            if (this.isLeftBevel) {
                for (let i = 0; i < this.bevelColumnCount; i++) {
                    width = width + this.getColumn(i).width + this.getVerticalThickness(i);
                }
                return width;
            }
            else {
                for (let i = this.flatColumnCount; i < this._columnCount; i++) {
                    width = width + this.getColumn(i).width + this.getVerticalThickness(i);
                }
                return width;
            }
        }
        return 0;
    }
    get _feetWidth() {
        let width = 4;
        if (this._data.socket.id.indexOf('metal-frame1') > -1
            || this._data.socket.id.indexOf('metal-frame2') > -1) {
            return 2;
        }
        if (this._data.socket.id.indexOf('4369') > -1) {
            return 1;
        }
        if (this._data.socket.id.indexOf('3816') > -1) {
            return 2.5;
        }
        switch (this._data.socket.id) {
            case 'wood-1':
            case 'NM-MDN926':
            case '4385':
                width = 18;
                break;
            case 'wood-5':
                width = this.width - 4;
                break;
            case 'NZ59-G0004-100':
            case '3291':
                width = 2;
                break;
            case 'wood-frame':
                width = 6;
                break;
            default:
                break;
        }
        return width;
    }
    get _feetDepth() {
        let depth = 4;
        if (this._data.socket.id.indexOf('metal-frame1') > -1
            || this._data.socket.id.indexOf('metal-frame2') > -1) {
            return 2;
        }
        if (this._data.socket.id.indexOf('4369') > -1) {
            return 6;
        }
        if (this._data.socket.id.indexOf('3816') > -1) {
            return 2.5;
        }
        switch (this._data.socket.id) {
            case 'wood-1':
            case 'NM-MDN926':
            case '4385':
                depth = 11;
                break;
            case 'wood-5':
                depth = this.depth - 2;
                break;
            case 'NZ59-G0004-100':
            case '3291':
                depth = 2;
                break;
            case 'wood-frame':
                depth = 5;
                break;
            default:
                break;
        }
        return depth;
    }
    // return column by index
    getColumn(index) {
        return this._columns[index] !== undefined
            ? this._columns[index]
            : undefined;
    }
    setData(data) {
        this._deskVertical = [];
        this._deskHorizontal = [];
        this._data = data;
    }
    getHorizontalThickness(isAroundDesk) {
        return isAroundDesk ? this._data.thickness.around : this._data.thickness.horizontal;
    }
    getVerticalThickness(index) {
        return index > 0 && index < this._columnCount ? this._data.thickness.vertical : this._data.thickness.around;
    }
    isHorizontalPositionUnderBevel(groupId, x) {
        if (!this.isBevel) {
            return false;
        }
        if (!this._isGroupLastInColumn(groupId)) {
            return false;
        }
        if (this.isLeftBevel) {
            return x >= this.bevelData.bottomX && x <= this.bevelData.topX;
        }
        else {
            return x >= this.bevelData.topX && x <= this.bevelData.bottomX;
        }
    }
    // check if group is on top of column
    _isGroupLastInColumn(groupId) {
        for (let i = 0; i < this._columns.length; i++) {
            const column = this._columns[i];
            const lastIndex = column.structure.length - 1;
            if (column.structure[lastIndex].door.group === groupId) {
                return true;
            }
        }
        return false;
    }
    _compare(a, b) {
        return (a.height > b.height) ? 1 : ((a.height < b.height) ? -1 : 0);
    }
    _isBevelVerticalDesk(i) {
        return this.isBevel
            ? this.isLeftBevel && i <= this.bevelColumnCount || !this.isLeftBevel && i > this.flatColumnCount
            : false;
    }
    /**
     * Return true if addition option exist and is selected
     * @param optionName
     * @private
     * @return boolean
     */
    _hasAdditionalOption(optionName) {
        if (typeof this._data.additionalOptions === "undefined") {
            return false;
        }
        if (typeof this._data.additionalOptions[optionName] === "undefined") {
            return false;
        }
        if (typeof this._data.additionalOptions[optionName].selected === "undefined") {
            return false;
        }
        return this._data.additionalOptions[optionName].selected;
    }
    get maxHorizontalThickness() {
        return Math.max(this.aroundThickness, this.getHorizontalThickness(false));
    }
    /**
     * Return Cover Strip data if exists
     * @public
     * @return CoverStripType | undefined
     */
    get coverstrip() {
        return this.hasCoverStrip ? {
            width: this.coverStripWidth / 10,
            thickness: this.coverStripThickness / 10,
            deskSizes: this.coverStripDeskSizes,
            longVerticalDeskSize: this.longVerticalCoverstripDeskSize,
            bevelDeskSize: this.bevelCoverstripDeskSize,
        } : undefined;
    }
    get longVerticalCoverstripDeskSize() {
        if (!this.hasCoverStrip) {
            return 0;
        }
        const size = Math.max(this.coverStripDeskSizes[0], this.coverStripDeskSizes[this.coverStripDeskSizes.length - 1]);
        return (size + (this.isBevel && this.flatColumnCount === 0 ? 0 : this.coverStripThickness)) / 10;
    }
    get bevelCoverstripDeskSize() {
        if (!this.hasCoverStrip || !this.isBevel) {
            return 0;
        }
        return (this.isLeftBevel ? this.coverStripDeskSizes[1] : this.coverStripDeskSizes[this.coverStripDeskSizes.length - 2]) / 10;
    }
    get coverStripThickness() {
        if (!this.hasCoverStrip) {
            return 0;
        }
        return this._data.additionalOptions.coverstrip.deskSizes[0].thickness;
    }
    get coverStripWidth() {
        if (!this.hasCoverStrip) {
            return 0;
        }
        return this._data.additionalOptions.coverstrip.deskSizes[0].width;
    }
    get coverStripDeskSizes() {
        if (!this.hasCoverStrip) {
            return [];
        }
        return this._data.additionalOptions.coverstrip.deskSizes.map((size) => size.length);
    }
    get hasCoverStrip() {
        return !(!this._hasAdditionalOption('coverstrip')
            || typeof this._data.additionalOptions.coverstrip.deskSizes === "undefined"
            || this._data.additionalOptions.coverstrip.deskSizes.length === 0
            || typeof this._data.additionalOptions.coverstrip.deskSizes[0].width === "undefined"
            || parseInt(this._data.additionalOptions.coverstrip.deskSizes[0].width) <= 0
            || typeof this._data.additionalOptions.coverstrip.deskSizes[0].thickness === "undefined"
            || parseInt(this._data.additionalOptions.coverstrip.deskSizes[0].thickness) <= 0);
    }
    get hasSlidingDoors() {
        return !!this._data.slidingDoors;
    }
    get slidingDoors() {
        return this._data.slidingDoors;
    }
    get slidingDoorsDepth() {
        if (!this.hasSlidingDoors) {
            return 0;
        }
        if (this.slidingDoors.type === SlidingDoorsType.DOUBLE_LEFT) {
            return SLIDING_DOOR_DESK_THICKNESS + SLIDING_DOOR_DEPTH_OFFSET;
        }
        return 2 * (SLIDING_DOOR_DESK_THICKNESS + SLIDING_DOOR_DEPTH_OFFSET);
    }
}
//# sourceMappingURL=Store.js.map